import { CreditCard } from "lucide-react"
import { cn } from "@/lib/utils/helpers"
import { SectionCard } from "@/components/ui/SectionCard"
import companyFormStyles from "@/features/company/styles/companyAdd.module.css"
import { usesTransferWizardCardLayout } from "../../../utils/transferPropertyCompanyWizard"
import type { TransferPropertyInfoSectionProps } from "./propertyInfoSectionTypes"
import { TransferPaymentField } from "./TransferPaymentField"

function splitPaymentsColumnClass(useCardLayout: boolean) {
  return useCardLayout
    ? "rounded-lg border border-slate-100 bg-slate-50/50 p-4 sm:p-5"
    : "rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/40 p-5 shadow-sm ring-1 ring-slate-100 sm:p-6"
}

export function TransferPropertySplitPaymentsSection({ ctx }: TransferPropertyInfoSectionProps) {
  const {
    fragment,
    propertyData,
    states,
    register,
    paymentMethod,
    paymentMethodNewProp,
    payeeName,
    payeeNameNew,
  } = ctx
  const propertyStaff = (propertyData?.property_staff as Record<string, unknown>[] | undefined) ?? []
  const useCardLayout = usesTransferWizardCardLayout(fragment)
  const columnClass = splitPaymentsColumnClass(useCardLayout)
  const primaryRowClass = useCardLayout ? "grid grid-cols-1 gap-6 md:grid-cols-3" : companyFormStyles.formRow
  const paperRow1Class = useCardLayout ? "grid grid-cols-1 gap-6 md:grid-cols-2" : companyFormStyles.formRow
  const paperRow2Class = useCardLayout
    ? "grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
    : companyFormStyles.formRow

  const originalPayments = (
    <div
      data-testid="transfer-process-split-payments-original-column"
      className={columnClass}
    >
      <h3 className="mb-4 border-b border-slate-100 pb-3 text-sm font-semibold tracking-tight text-slate-900">
        Original property
      </h3>
      <div className="space-y-5">
        <div data-testid="transfer-process-split-payments-original-primary-row" className={primaryRowClass}>
          <TransferPaymentField useCardLayout={useCardLayout}>
            <label className={companyFormStyles.formLabel} htmlFor="transfer-split-pay-method-orig">
              Method
            </label>
            <select
              id="transfer-split-pay-method-orig"
              data-testid="transfer-process-split-payments-original-method-select"
              className={companyFormStyles.customFormControl}
              {...register("payment_method")}
            >
              <option value="">Select Type</option>
              <option value="ACH">ACH</option>
              <option value="PAPER_CHECK">Paper</option>
            </select>
          </TransferPaymentField>
          <TransferPaymentField useCardLayout={useCardLayout}>
            <label className={companyFormStyles.formLabel} htmlFor="transfer-split-pay-verify-orig">
              Verified
            </label>
            <select
              id="transfer-split-pay-verify-orig"
              data-testid="transfer-process-split-payments-original-verify-select"
              className={companyFormStyles.customFormControl}
              {...register("verify")}
            >
              <option value="">Type</option>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </TransferPaymentField>
          <TransferPaymentField useCardLayout={useCardLayout}>
            <label className={companyFormStyles.formLabel} htmlFor="transfer-split-pay-to-orig">
              Pay To
            </label>
            <select
              id="transfer-split-pay-to-orig"
              data-testid="transfer-process-split-payments-original-pay-to-select"
              className={companyFormStyles.customFormControl}
              {...register("pay_to")}
            >
              <option value="">Select</option>
              <option value="COMPANY">Company</option>
              <option value="PROPERTY">Property</option>
            </select>
          </TransferPaymentField>
        </div>
        {paymentMethod === "ACH" && (
          <div data-testid="transfer-process-split-payments-original-ach-row" className={primaryRowClass}>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label className={companyFormStyles.formLabel}>ACH Routing</label>
              <input
                type="text"
                data-testid="transfer-process-split-payments-original-routing-input"
                className={companyFormStyles.customFormControl}
                placeholder="Routing Number"
                {...register("routing_number")}
              />
            </TransferPaymentField>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label className={companyFormStyles.formLabel}>ACH Account</label>
              <input
                type="text"
                data-testid="transfer-process-split-payments-original-account-input"
                className={companyFormStyles.customFormControl}
                placeholder="Account Number"
                {...register("account_number")}
              />
            </TransferPaymentField>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label className={companyFormStyles.formLabel}>Account Type</label>
              <select
                data-testid="transfer-process-split-payments-original-account-type-select"
                className={companyFormStyles.customFormControl}
                {...register("property_method")}
              >
                <option value="">Account Type</option>
                <option value="Checkings">Checkings</option>
                <option value="Savings">Savings</option>
              </select>
            </TransferPaymentField>
          </div>
        )}
        {paymentMethod === "PAPER_CHECK" && (
          <>
            <div data-testid="transfer-process-split-payments-original-paper-row-1" className={paperRow1Class}>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>ATTN Payee</label>
                <select
                  data-testid="transfer-process-split-payments-original-payee-select"
                  className={companyFormStyles.customFormControl}
                  {...register("payee_name")}
                >
                  <option value="">Select ATTN Contact</option>
                  <option value="LEASING">Leasing Office</option>
                  {propertyStaff.map((p) => (
                    <option key={String(p.user_id)} value={String(p.user_id)}>
                      {String(p.first_name ?? "")} {String(p.last_name ?? "")}
                    </option>
                  ))}
                  <option value="OTHER">Other</option>
                </select>
                {payeeName === "OTHER" && (
                  <input
                    type="text"
                    data-testid="transfer-process-split-payments-original-other-payee-input"
                    className={cn(companyFormStyles.customFormControl, "mt-2")}
                    {...register("other_payee_name")}
                  />
                )}
              </TransferPaymentField>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel} htmlFor="transfer-split-pay-addr-orig">
                  Street Address
                </label>
                <textarea
                  id="transfer-split-pay-addr-orig"
                  data-testid="transfer-process-split-payments-original-payment-address-textarea"
                  rows={2}
                  className={cn(companyFormStyles.customFormControl, "min-h-[72px] resize-y")}
                  placeholder="Payment Address"
                  {...register("payment_address")}
                />
              </TransferPaymentField>
            </div>
            <div data-testid="transfer-process-split-payments-original-paper-row-2" className={paperRow2Class}>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>Suite/Shop</label>
                <input
                  type="text"
                  data-testid="transfer-process-split-payments-original-suite-input"
                  className={companyFormStyles.customFormControl}
                  placeholder="Suite/Shop"
                  {...register("payment_shop")}
                />
              </TransferPaymentField>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>City</label>
                <input
                  type="text"
                  data-testid="transfer-process-split-payments-original-payment-city-input"
                  className={companyFormStyles.customFormControl}
                  placeholder="Payment City"
                  {...register("payment_city")}
                />
              </TransferPaymentField>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>State</label>
                <select
                  data-testid="transfer-process-split-payments-original-payment-state-select"
                  className={companyFormStyles.customFormControl}
                  {...register("payment_state")}
                >
                  <option value="">State</option>
                  {states.map((s) => (
                    <option key={`split-pay-orig-${s.state_code}`} value={s.state_code}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </TransferPaymentField>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>Zipcode</label>
                <input
                  type="text"
                  data-testid="transfer-process-split-payments-original-payment-zip-input"
                  className={companyFormStyles.customFormControl}
                  placeholder="Payment Zipcode"
                  {...register("payment_zipcode")}
                />
              </TransferPaymentField>
            </div>
          </>
        )}
      </div>
    </div>
  )

  const newPayments = (
    <div
      data-testid="transfer-process-split-payments-new-column"
      className={columnClass}
    >
      <h3 className="mb-4 border-b border-slate-100 pb-3 text-sm font-semibold tracking-tight text-slate-900">
        New property
      </h3>
      <div className="space-y-5">
        <div data-testid="transfer-process-split-payments-new-primary-row" className={primaryRowClass}>
          <TransferPaymentField useCardLayout={useCardLayout}>
            <label className={companyFormStyles.formLabel} htmlFor="transfer-split-pay-method-new">
              Method
            </label>
            <select
              id="transfer-split-pay-method-new"
              data-testid="transfer-process-split-payments-new-method-select"
              className={companyFormStyles.customFormControl}
              {...register("newpropertyinfo.payment_method")}
            >
              <option value="">Select Type</option>
              <option value="ACH">ACH</option>
              <option value="PAPER_CHECK">Paper</option>
            </select>
          </TransferPaymentField>
          <TransferPaymentField useCardLayout={useCardLayout}>
            <label className={companyFormStyles.formLabel} htmlFor="transfer-split-pay-verify-new">
              Verified
            </label>
            <select
              id="transfer-split-pay-verify-new"
              data-testid="transfer-process-split-payments-new-verify-select"
              className={companyFormStyles.customFormControl}
              {...register("newpropertyinfo.verify")}
            >
              <option value="">Type</option>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </TransferPaymentField>
          <TransferPaymentField useCardLayout={useCardLayout}>
            <label className={companyFormStyles.formLabel} htmlFor="transfer-split-pay-to-new">
              Pay To
            </label>
            <select
              id="transfer-split-pay-to-new"
              data-testid="transfer-process-split-payments-new-pay-to-select"
              className={companyFormStyles.customFormControl}
              {...register("newpropertyinfo.pay_to")}
            >
              <option value="">Select</option>
              <option value="COMPANY">Company</option>
              <option value="PROPERTY">Property</option>
            </select>
          </TransferPaymentField>
        </div>
        {paymentMethodNewProp === "ACH" && (
          <div data-testid="transfer-process-split-payments-new-ach-row" className={primaryRowClass}>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label className={companyFormStyles.formLabel}>ACH Routing</label>
              <input
                type="text"
                data-testid="transfer-process-split-payments-new-routing-input"
                className={companyFormStyles.customFormControl}
                placeholder="Routing Number"
                {...register("newpropertyinfo.routing_number")}
              />
            </TransferPaymentField>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label className={companyFormStyles.formLabel}>ACH Account</label>
              <input
                type="text"
                data-testid="transfer-process-split-payments-new-account-input"
                className={companyFormStyles.customFormControl}
                placeholder="Account Number"
                {...register("newpropertyinfo.account_number")}
              />
            </TransferPaymentField>
            <TransferPaymentField useCardLayout={useCardLayout}>
              <label className={companyFormStyles.formLabel}>Account Type</label>
              <select
                data-testid="transfer-process-split-payments-new-account-type-select"
                className={companyFormStyles.customFormControl}
                {...register("newpropertyinfo.property_method")}
              >
                <option value="">Select Type</option>
                <option value="Checkings">Checkings</option>
                <option value="Savings">Savings</option>
              </select>
            </TransferPaymentField>
          </div>
        )}
        {paymentMethodNewProp === "PAPER_CHECK" && (
          <>
            <div data-testid="transfer-process-split-payments-new-paper-row-1" className={paperRow1Class}>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>ATTN Payee</label>
                <select
                  data-testid="transfer-process-split-payments-new-payee-select"
                  className={companyFormStyles.customFormControl}
                  {...register("newpropertyinfo.payee_name")}
                >
                  <option value="">Select ATTN Contact</option>
                  <option value="LEASING">Leasing Office</option>
                  {propertyStaff.map((p) => (
                    <option key={`split-new-${String(p.user_id)}`} value={String(p.user_id)}>
                      {String(p.first_name ?? "")} {String(p.last_name ?? "")}
                    </option>
                  ))}
                  <option value="OTHER">Other</option>
                </select>
                {payeeNameNew === "OTHER" && (
                  <input
                    type="text"
                    data-testid="transfer-process-split-payments-new-other-payee-input"
                    className={cn(companyFormStyles.customFormControl, "mt-2")}
                    {...register("newpropertyinfo.other_payee_name")}
                  />
                )}
              </TransferPaymentField>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel} htmlFor="transfer-split-pay-addr-new">
                  Street Address
                </label>
                <textarea
                  id="transfer-split-pay-addr-new"
                  data-testid="transfer-process-split-payments-new-payment-address-textarea"
                  rows={2}
                  className={cn(companyFormStyles.customFormControl, "min-h-[72px] resize-y")}
                  placeholder="Payment Address"
                  {...register("newpropertyinfo.payment_address")}
                />
              </TransferPaymentField>
            </div>
            <div data-testid="transfer-process-split-payments-new-paper-row-2" className={paperRow2Class}>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>Suite/Shop</label>
                <input
                  type="text"
                  data-testid="transfer-process-split-payments-new-suite-input"
                  className={companyFormStyles.customFormControl}
                  placeholder="Suite/Shop"
                  {...register("newpropertyinfo.payment_shop")}
                />
              </TransferPaymentField>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>City</label>
                <input
                  type="text"
                  data-testid="transfer-process-split-payments-new-payment-city-input"
                  className={companyFormStyles.customFormControl}
                  placeholder="Payment City"
                  {...register("newpropertyinfo.payment_city")}
                />
              </TransferPaymentField>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>State</label>
                <select
                  data-testid="transfer-process-split-payments-new-payment-state-select"
                  className={companyFormStyles.customFormControl}
                  {...register("newpropertyinfo.payment_state")}
                >
                  <option value="">State</option>
                  {states.map((s) => (
                    <option key={`split-pay-new-${s.state_code}`} value={s.state_code}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </TransferPaymentField>
              <TransferPaymentField useCardLayout={useCardLayout}>
                <label className={companyFormStyles.formLabel}>Zipcode</label>
                <input
                  type="text"
                  data-testid="transfer-process-split-payments-new-payment-zip-input"
                  className={companyFormStyles.customFormControl}
                  placeholder="Payment Zipcode"
                  {...register("newpropertyinfo.payment_zipcode")}
                />
              </TransferPaymentField>
            </div>
          </>
        )}
      </div>
    </div>
  )

  const paymentsGrid = (
    <div
      data-testid="transfer-process-split-payments-grid"
      className="grid gap-6 lg:grid-cols-2 lg:gap-10"
    >
      {originalPayments}
      {newPayments}
    </div>
  )

  if (useCardLayout) {
    return (
      <SectionCard
        icon={CreditCard}
        title="Payments Preferences"
        testId="transfer-process-split-payments-section"
        titleTestId="transfer-process-split-payments-section-title"
      >
        {paymentsGrid}
      </SectionCard>
    )
  }

  return (
    <>
      <h2
        data-testid="transfer-process-split-payments-section-title"
        className={companyFormStyles.sectionTitle}
      >
        Payments Preferences
      </h2>
      {paymentsGrid}
    </>
  )
}
